/**
 * Supabase persistence for vehicle reminders.
 * Write functions THROW on failure so callers can queue retries via writeQueue.
 * Read functions catch errors gracefully and return empty arrays.
 */
import { supabase } from '../config/supabase';

/* ───────────────────── Write ───────────────────── */

/**
 * Upsert a reminder to Supabase via RPC (bypasses RLS for collaborators).
 * THROWS on failure so callers can queue retries.
 */
// UUID v4 pattern check
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function saveReminderToSupabase(userId, vehicleId, reminder) {
  if (!userId) {
    console.warn('[PM-Write] No userId, skipping reminder save');
    return;
  }

  // Ensure the ID is a valid UUID (localStorage may have non-UUID IDs from older versions)
  const reminderId = UUID_RE.test(reminder.id) ? reminder.id : crypto.randomUUID();

  const { error } = await supabase.rpc('upsert_reminder', {
    r_id: reminderId,
    r_user_id: userId,
    r_vehicle_id: vehicleId,
    r_title: reminder.title,
    r_description: reminder.description || null,
    r_due_date: reminder.dueDate,
    r_due_time: reminder.dueTime || null,
    r_status: reminder.status || 'pending',
    r_completed_at: reminder.completedAt || null,
  });

  if (error) throw error;
  console.log('[PM-Write] Saved reminder to Supabase:', reminder.id);
}

/**
 * Delete a reminder from Supabase via RPC (bypasses RLS for collaborators).
 * THROWS on failure so callers can queue retries.
 */
export async function deleteReminderFromSupabase(reminderId) {
  if (!reminderId) return;

  const { error } = await supabase.rpc('delete_reminder', {
    r_id: reminderId,
  });

  if (error) throw error;
  console.log('[PM-Write] Deleted reminder from Supabase:', reminderId);
}

/* ───────────────────── Read ───────────────────── */

/**
 * Load all reminders for a user from Supabase (for hydration).
 * Uses RLS — only returns reminders the user owns.
 * Returns raw Supabase rows.
 */
export async function loadUserReminders(userId) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('reminders')
      .select('*')
      .eq('user_id', userId)
      .order('due_date', { ascending: true });

    if (error) throw error;
    console.log('[PM-Read] Loaded', data?.length || 0, 'reminders from Supabase');
    return data || [];
  } catch (err) {
    console.error('[PM-Read] Failed to load reminders:', err.message);
    return [];
  }
}

/**
 * Load all reminders for a specific vehicle via RPC (bypasses RLS).
 * Used for shared garage vehicles where the logged-in user may not be the owner.
 * Returns raw Supabase rows.
 */
export async function loadVehicleReminders(vehicleId) {
  if (!vehicleId) return [];
  try {
    const { data, error } = await supabase.rpc('get_vehicle_reminders', {
      vehicle_uuid: vehicleId,
    });
    if (error) throw error;
    console.log('[PM-Read] Loaded', data?.length || 0, 'reminders for vehicle:', vehicleId);
    return data || [];
  } catch (err) {
    console.error('[PM-Read] loadVehicleReminders failed:', err.message);
    return [];
  }
}

/* ───────────────────── Mapping ───────────────────── */

/**
 * Map a Supabase reminders row to the localStorage reminder format.
 * Used in hydration, invite acceptance, and on-demand loading for shared vehicles.
 */
export function mapReminderToLocal(row) {
  return {
    id: row.id,
    vehicleId: row.vehicle_id,
    userId: row.user_id,
    title: row.title,
    description: row.description || '',
    dueDate: row.due_date,
    dueTime: row.due_time || null,
    status: row.status || 'pending',
    completedAt: row.completed_at || null,
    createdAt: row.created_at,
  };
}
